/**
 * Раздел «Баланс»: белки, жиры и углеводы остатком.
 *
 * Калорийности здесь нет намеренно: **дефицит про калории, баланс
 * про БЖУ** (решение владельца 26.10). Дефицит задаётся полем
 * «Дефицит / профицит, %» в разделе целей, а сюда приходит уже
 * посчитанной калорийностью — от неё считаются доли.
 *
 * Считается одинаково на сервере и в браузере — сервер рисует первый
 * экран, браузер пересчитывает при перетаскивании ручки. Поэтому
 * формулы лежат здесь, а не в маршруте: разъехавшись, они дали бы
 * разные числа до и после первого движения ползунка.
 *
 * Коридоры приходят из `bod-users` полем `userStatsRanges`: концы
 * ползунка — это min и max цели, выйти за них нельзя по устройству
 * контрола (bod-knowledge decisions/balance-controls.md).
 */
const PROTEIN_KKAL = 4;
const FAT_KKAL = 9;
const CARBOHYDRATE_KKAL = 4;

// Шаг ручки. Белок в граммах на килограмм, жиры и калорийность —
// долями, поэтому шаги разные: у белка 0,05 г/кг это около четырёх
// граммов на человека, у жиров 1 процентный пункт.
const STEP = {
    protein: 0.05,
    fat: 0.01,
};

/**
 * Числа дня при заданных модификаторе, белке и доле жиров.
 * Углеводы — остаток от уже округлённых белка и жиров, как в расчёте
 * на сервере: иначе сумма килокалорий не сойдётся с тем, что на экране.
 */
function getNumbers({ maintenanceCalories, calculationWeight, calorieModifier, protein, fat }) {
    const calories = Math.round(maintenanceCalories * (1 + calorieModifier));
    const proteins = Math.round(calculationWeight * protein);
    const fatGrams = Math.round(calories * fat / FAT_KKAL);
    const carbohydrate = Math.round(
        (calories - PROTEIN_KKAL * proteins - FAT_KKAL * fatGrams) / CARBOHYDRATE_KKAL
    );
    return { calories, proteins, fat: fatGrams, carbohydrate };
}

// Доля в процентах от калорийности. Подпись — голый процент: что он
// значит, объяснит подсказка при наведении (решение владельца 26.10,
// макет будет позже). Нужна только для показа, поэтому
// округляется до целого: сумма трёх долей из-за этого иногда даёт 99
// или 101, и это нормально.
function share(kkalPerGram, grams, calories) {
    if (!calories) return 0;
    return Math.round(kkalPerGram * grams / calories * 100);
}

// Ползунок строится, только когда коридор задан с обеих сторон и шире
// нуля. У некоторых строк справочника диапазона нет вовсе — например
// «набрать мышцы» при ИМТ 40 и выше: там одно значение без настройки.
function isAdjustable(range) {
    if (!range) return false;
    const { min, max } = range;
    if (min === null || min === undefined || max === null || max === undefined) return false;
    return max > min;
}

/**
 * @param {object} ranges — `userStatsRanges` пользователя
 * @returns {object|undefined} модель раздела или ничего, если считать не из чего
 */
function buildBalance(ranges) {
    if (!ranges || !ranges.protein || !ranges.fat || !ranges.calorieModifier) return undefined;
    const { maintenanceCalories, calculationWeight } = ranges;
    if (!maintenanceCalories || !calculationWeight) return undefined;

    const numbers = getNumbers({
        maintenanceCalories,
        calculationWeight,
        calorieModifier: ranges.calorieModifier.value,
        protein: ranges.protein.value,
        fat: ranges.fat.value,
    });

    return {
        maintenanceCalories,
        calculationWeight,
        // Текущий дефицит долей: от него считается калорийность, а значит
        // и доли БЖУ. Меняется полем в разделе целей, поэтому отдаём
        // отдельно — раздел «Баланс» им не управляет, но от него зависит.
        calorieModifier: ranges.calorieModifier.value,
        protein: {
            key: 'protein',
            label: 'Белки, норма дня, г',
            grams: numbers.proteins,
            share: share(PROTEIN_KKAL, numbers.proteins, numbers.calories),
            shareSuffix: '%',
            adjustable: isAdjustable(ranges.protein),
            min: ranges.protein.min,
            max: ranges.protein.max,
            default: ranges.protein.default,
            value: ranges.protein.value,
            step: STEP.protein,
        },
        fat: {
            key: 'fat',
            label: 'Жиры, норма дня, г',
            grams: numbers.fat,
            share: share(FAT_KKAL, numbers.fat, numbers.calories),
            shareSuffix: '%',
            adjustable: isAdjustable(ranges.fat),
            min: ranges.fat.min,
            max: ranges.fat.max,
            default: ranges.fat.default,
            value: ranges.fat.value,
            step: STEP.fat,
        },
        // Углеводы без ползунка: ими нельзя управлять напрямую, иначе три
        // числа начнут спорить за одну сумму. Это амортизатор.
        carbohydrate: {
            key: 'carbohydrate',
            label: 'Углеводы, норма дня, г',
            grams: numbers.carbohydrate,
            share: share(CARBOHYDRATE_KKAL, numbers.carbohydrate, numbers.calories),
            shareSuffix: '%',
            adjustable: false,
        },
    };
}

module.exports = { buildBalance, getNumbers, share, PROTEIN_KKAL, FAT_KKAL, CARBOHYDRATE_KKAL };
